import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { ChatMemberUnion } from '@airgram/web';
import { TranslateService } from '@ngx-translate/core';
import { EnvService } from '@src/app/modules/env';
import { AlertService, UserService } from '@src/core/services';
import { GroupUI, ViewMode, UserUI, Multimedia } from '@src/models';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import {
  CommitteeUserSubscriptionsService,
  SubscriptionForUsers,
  SubscriptionsForUsersService,
  UsersSearchParameters,
} from '@src/api';
import { BaseResizableComponent } from '@src/app/modules/base';

// TODO: вынести в types.ts
interface FormData {
  id: string;
  name: string;
  description: string;
  members: UserUI[];
}

type FormDataControls = { [key in keyof FormData]: AbstractControl };

@Component({
  selector: 'app-group-info-view',
  templateUrl: './group-info-view.component.html',
  styleUrls: ['./group-info-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupInfoViewComponent extends BaseResizableComponent implements OnChanges {
  public readonly env = inject(EnvService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly subscriptionsForUsersService = inject(SubscriptionsForUsersService);
  private readonly committeeUserSubscriptionsService = inject(CommitteeUserSubscriptionsService);

  @Input() mode: ViewMode = 'view';
  @Input() data?: GroupUI;
  @Input() members?: ChatMemberUnion[];
  @Input() isChannel?: boolean;
  @Input() memberCount?: number;
  @Input() initFilter?: UsersSearchParameters;
  @Input() loading = false;
  @Input() membersLoading = false;
  @Input() allowEditing?: boolean | null = false;
  @Input() allowDeleting?: boolean | null = false;
  @Input() allowSubscribing?: boolean | null = false;
  @Input() allowSpecialFieldsViewing?: boolean | null = false;
  @Output() startEditing = new EventEmitter<void>();
  @Output() saved = new EventEmitter<GroupUI>();
  @Output() canceled = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<void>();
  @Output() deletePhoto = new EventEmitter<void>();
  @Output() subscribed = new EventEmitter<void>();
  @Output() unsubscribed = new EventEmitter<void>();
  @Output() updateMembers = new EventEmitter<void>();

  infoForm: UntypedFormGroup;
  newPhoto?: Multimedia;
  authUser$ = this.userService.authUser$;
  synchronizing = false;

  // TODO: вынести в константу
  readonly DESCRIPTION_LIMITS = 255;

  private readonly confirmCancelEditingDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.undoEditHeader'),
      size: 's',
      closeable: false,
    },
  );

  constructor() {
    super();

    this.infoForm = new UntypedFormGroup(<FormDataControls>{
      id: new UntypedFormControl(undefined),
      name: new UntypedFormControl('', [Validators.required, Validators.maxLength(255)]),
      description: new UntypedFormControl('', Validators.maxLength(255)),
      isDefault: new UntypedFormControl(false),
      members: new UntypedFormControl(null),
    });
  }

  get isLoggedMessenger(): boolean {
    return this.env.isLoggedMessenger();
  }

  get isActiveTab(): boolean {
    return this.env.isActiveTab();
  }

  get isLoadedAllChatsMessenger(): boolean {
    return this.env.isLoadedAllChatsMessenger();
  }

  get isDefaultControl() {
    return this.infoForm.controls.isDefault;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.newPhoto = undefined;

      if (this.data) {
        this.setFormData();
      } else {
        this.infoForm.reset();
      }
    }

    this.cdr.markForCheck();
  }

  getMembers() {
    this.updateMembers.emit();
  }

  onUserIdChange(userId: string) {
    this.router.navigate(['association-users', userId]);
  }

  onClickEditButton(): void {
    if (!this.isActiveTab) {
      this.env.viewManyTabsDialog();
      return;
    }

    if (!this.isLoggedMessenger) {
      this.env.viewConfirmLoginMessenger(() => this.startEditing.emit());
      return;
    }

    this.startEditing.emit();
  }

  onClickSaveButton(): void {
    this.infoForm.markAllAsTouched();

    if (this.infoForm.invalid) {
      this.alertService.error(this.translateService.instant('common.alerts.errors.fillRequired'));
      return;
    }

    // TODO: вынести в отдельный метод mapFormDataToGroupUI
    const { members } = this.infoForm.value as FormData;

    const membersTelegramIds = members?.map(member => member.telegramId).filter(id => !!id) as number[];
    const membersIds = members?.map(member => member.id).filter(id => !!id) as string[];
    const unregistredMembersIds = members
      ?.filter(member => !member.telegramId)
      .map(member => member.id)
      .filter(id => id !== undefined) as string[];

    this.data = {
      ...this.data,
      ...this.infoForm.value,
      ...{ photo: this.newPhoto?.file },
      ...{ chatTypeId: this.data?.isChannel ? 2 : 1 },
      ...{ membersTelegramIds, membersIds, unregistredMembersIds },
    };

    this.saved.emit(this.data);
  }

  onClickCancelButton(): void {
    this.confirmCancelEditingDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          this.setFormData();
          this.canceled.emit();
        }
      },
    });
  }

  onPhotoChange(newPhoto?: Multimedia): void {
    this.newPhoto = newPhoto;
    this.cdr.markForCheck();
  }

  onPhotoDelete(): void {
    this.newPhoto = undefined;
    this.deletePhoto.emit();
    this.cdr.markForCheck();
  }

  onClickSubscribeButton(): void {
    if (!this.isLoggedMessenger && this.data?.codeName) {
      this.env.openLink(this.data?.codeName);
      return;
    }

    if (!this.isActiveTab) {
      this.env.viewManyTabsDialog();
      return;
    }

    this.subscribed.emit();
  }

  onClickUnsubscribeButton(): void {
    if (!this.isLoggedMessenger && this.data?.codeName) {
      this.env.openLink(this.data?.codeName);
      return;
    }

    if (!this.isActiveTab) {
      this.env.viewManyTabsDialog();
      return;
    }

    this.unsubscribed.emit();
  }

  onClickChatButton(chat: GroupUI): void {
    if (!chat?.codeName || !chat?.id || !chat?.chatId) {
      return;
    }

    if (this.env.isBot || !this.isLoggedMessenger) {
      this.env.openLink(chat.codeName);
    } else if (!this.isActiveTab) {
      this.env.viewManyTabsDialog();
    } else if (this.isLoadedAllChatsMessenger) {
      this.env.gotoChat(chat.chatId);
    }
  }

  onClickDeleteButton(): void {
    if (!this.isActiveTab) {
      this.env.viewManyTabsDialog();
      return;
    }

    this.deleted.emit();
  }

  get isSynchronizeButtonVisible() {
    if (this.data?.ownerId && this.authUser$.value?.id) {
      return this.data.ownerId === this.authUser$.value.id || this.data.active;
    }

    return false;
  }

  onClickSynchronizeButton() {
    if (!this.isActiveTab) {
      this.env.viewManyTabsDialog();
      return;
    }

    if (!this.isLoggedMessenger) {
      this.env.viewConfirmLoginMessenger(() => this.synchronizeUsers());
      return;
    }

    this.synchronizeUsers();
  }

  private async synchronizeUsers() {
    if (this.data?.id && this.members) {
      this.synchronizing = true;

      const telegramIds = this.members.reduce<number[]>((acc, item) => {
        if (item.memberId._ === 'messageSenderUser') {
          acc.push(item.memberId.userId);
        }
        return acc;
      }, []);

      try {
        const users = await lastValueFrom(this.userService.getUsersData(telegramIds));

        const options: SubscriptionForUsers = {
          subscriptionId: this.data?.id,
          userIds: users.map(item => item.id!),
        };

        if (this.isCommittee) {
          await lastValueFrom(this.committeeUserSubscriptionsService.updateCommitteeSubscriptionsForUsers(options));
        } else {
          await lastValueFrom(this.subscriptionsForUsersService.updateSubscriptionForUsers(options));
        }

        this.alertService.success(
          this.translateService.instant('components.groupInfo.alerts.successes.updateSubscriptionForUsers'),
        );
      } finally {
        this.synchronizing = false;
        this.cdr.markForCheck();
      }
    }
  }

  private get isCommittee() {
    return !!this.data?.committeeId;
  }

  private setFormData(): void {
    if (!this.data) return;

    const { id, name, description, isDefault } = this.data;
    this.infoForm.patchValue({ id, name, description, isDefault: !!isDefault });
  }
}
